import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useFlipSettings } from "@/lib/flip";

const box = "h-8 w-20 rounded-md border border-slate-300 bg-white px-2 font-mono text-sm";
const clean = (v) => v.replace(/[^\d.]/g, "");

// Default ROI target for flips, and how shipping is counted.
export default function FlipSettingsForm() {
  const [s, save] = useFlipSettings();
  const [f, setF] = useState(null);
  useEffect(() => { setF({ roi: s.roi_pct ?? "", profit: s.min_profit ?? "", charge: s.charge_shipping, mode: s.ship_mode, flat: s.flat_ship ?? 8 }); }, [s]);
  if (!f) return null;
  const submit = async (next = f) => {
    try {
      await save({ roi_pct: next.roi === "" ? null : Number(next.roi), min_profit: next.profit === "" ? null : Number(next.profit),
        charge_shipping: next.charge, ship_mode: next.mode, flat_ship: Number(next.flat || 0) });
      toast.success("ROI target saved");
    } catch { toast.error("Couldn't save target"); }
  };
  const set = (patch, now = false) => { const next = { ...f, ...patch }; setF(next); if (now) submit(next); };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs" data-testid="flip-settings">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Target className="h-4 w-4 text-emerald-600" /> ROI target for flips</p>
      <p className="mt-0.5 text-[11px] text-slate-500">Research shows the most you can pay and still hit this. Set one or both; the lower buy price wins.</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5">Return <input value={f.roi} onChange={(e) => set({ roi: clean(e.target.value) })} onBlur={() => submit()} inputMode="decimal" placeholder="off" className={box} data-testid="flip-roi-input" />%</label>
        <label className="flex items-center gap-1.5">Profit $<input value={f.profit} onChange={(e) => set({ profit: clean(e.target.value) })} onBlur={() => submit()} inputMode="decimal" placeholder="off" className={box} data-testid="flip-profit-input" /> per item</label>
      </div>
      <label className="mt-3 flex items-center justify-between gap-3">
        <span className="font-medium text-slate-700">I charge buyers for shipping</span>
        <Switch checked={f.charge} onCheckedChange={(v) => set({ charge: v }, true)} data-testid="flip-charge-shipping" />
      </label>
      {!f.charge && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="flip-ship-mode">
          <span className="text-slate-500">I pay shipping:</span>
          {[["estimate", "Estimate per item"], ["flat", "Flat rate"]].map(([k, t]) => (
            <button key={k} type="button" onClick={() => set({ mode: k }, true)} data-testid={`flip-ship-${k}`}
              className={`rounded-full border px-2.5 py-1 font-semibold transition-colors ${f.mode === k ? "border-[#0053DA] bg-[#0053DA] text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{t}</button>
          ))}
          {f.mode === "flat" && <label className="flex items-center gap-1">$<input value={f.flat} onChange={(e) => set({ flat: clean(e.target.value) })} onBlur={() => submit()} inputMode="decimal" className={box} data-testid="flip-flat-input" /></label>}
        </div>
      )}
    </div>
  );
}
